import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import SocialAuthButtons from "@/components/SocialAuthButtons";
import TurnstileWidget from "@/components/TurnstileWidget";

const TURNSTILE_ENABLED = !!process.env.REACT_APP_TURNSTILE_SITE_KEY;

export default function Register() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    email: "",
    password: "",
    confirm_password: "",
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkInbox, setCheckInbox] = useState(null); // {email} when submitted
  const [resending, setResending] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (form.password !== form.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }
    if (!acceptedTerms) {
      toast.error("Please read and accept the Privacy Policy and Terms of Use.");
      return;
    }
    if (TURNSTILE_ENABLED && !turnstileToken) {
      toast.error("Please complete the verification challenge.");
      return;
    }
    setLoading(true);
    try {
      // Only email + password are sent. Profile details (name, contact, school,
      // course) are collected at /student/profile/complete before payment.
      const r = await axios.post(`${API}/auth/register`, {
        email: form.email,
        password: form.password,
        turnstile_token: turnstileToken || null,
        accepted_terms: true,
      });
      setCheckInbox({ email: form.email, email_sent: r?.data?.email_sent });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
      turnstileRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  const resendEmail = async () => {
    if (!checkInbox?.email) return;
    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email: checkInbox.email });
      toast.success("Verification email sent. Please check your inbox.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend email");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#F8FAFC]">
      <div className="hidden md:block relative">
        <img src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=1740&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A8A]/85 to-[#2563EB]/70" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_smart-prep-board/artifacts/c8fzp7x9_PrepMasters%20New%20Logo%20Night.png"
              alt="PrepMaster"
              className="h-[1.875rem] w-auto"
            />
          </Link>
          <div>
            <div className="font-display text-3xl font-bold leading-tight">Join thousands preparing for the board &amp; civil service the smart way.</div>
            <p className="mt-4 text-white/80">Choose your target exam at sign-up — your account will be reviewed by an admin before access is granted.</p>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 py-10">
        <Card className="w-full max-w-md p-8 border-slate-200">
          {checkInbox ? (
            <div className="text-center" data-testid="register-check-inbox">
              <div className="mx-auto h-16 w-16 rounded-2xl bg-emerald-100 grid place-items-center mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-700"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
              </div>
              <h1 className="font-display text-2xl font-bold tracking-tight">Check your inbox</h1>
              <p className="text-slate-600 mt-2">
                We sent a verification link to <span className="font-semibold text-slate-900">{checkInbox.email}</span>.
                Click it to confirm your email — then an admin will review your account for approval.
              </p>
              <div className="mt-5 p-3 rounded-lg bg-amber-50 border border-amber-200 text-left text-sm text-amber-800">
                Don't see it? Check your <span className="font-semibold">Spam</span> or <span className="font-semibold">Promotions</span> folder. The link expires in 24 hours.
              </div>
              <Button
                variant="outline"
                className="w-full mt-5 h-11"
                onClick={resendEmail}
                disabled={resending}
                data-testid="register-resend-btn"
              >
                {resending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Resend verification email
              </Button>
              <Link
                to="/login"
                className="block mt-3 text-sm text-blue-700 font-semibold hover:underline"
                data-testid="register-back-to-login"
              >
                Back to login
              </Link>
            </div>
          ) : (
          <>
          <h1 className="font-display text-3xl font-bold tracking-tight">Create your account</h1>
          <p className="text-slate-600 mt-1">Start preparing for your board exam.</p>

          <div className="mt-6">
            <SocialAuthButtons hideInternalCheckbox={true} externalAcceptedTerms={acceptedTerms} />
          </div>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
            <span className="text-xs uppercase tracking-wider text-slate-500">or register with email</span>
            <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
          </div>

          <form className="space-y-3" onSubmit={submit}>
            <div>
              <Label>Email *</Label>
              <Input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email-input" autoComplete="email" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Password *</Label>
                <Input type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password-input" autoComplete="new-password" />
              </div>
              <div>
                <Label>Confirm *</Label>
                <Input type="password" required minLength={8} value={form.confirm_password} onChange={(e) => setForm({ ...form, confirm_password: e.target.value })} data-testid="register-confirm-input" autoComplete="new-password" />
              </div>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed pt-1" data-testid="register-profile-note">
              You can complete your full name, contact number, school, and course selection later — you'll be prompted to fill these in before paying for a course.
            </p>

            {/* Privacy Policy + Terms of Use acceptance — required by law
                and by Apple / Google store policies. The two links open in
                a new tab so the student can review without losing form state. */}
            <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 p-3 mt-2">
              <label className="flex items-start gap-2 cursor-pointer" data-testid="register-accept-terms-label">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-700 focus:ring-blue-500"
                  data-testid="register-accept-terms-checkbox"
                />
                <span className="text-xs text-slate-700 dark:text-slate-200 leading-relaxed">
                  I have read and agree to the{" "}
                  <Link
                    to="/legal/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 font-semibold hover:underline"
                    data-testid="register-privacy-link"
                  >
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link
                    to="/legal/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-700 font-semibold hover:underline"
                    data-testid="register-terms-link"
                  >
                    Terms of Use
                  </Link>
                  .
                </span>
              </label>
            </div>

            <Button type="submit" className="w-full bg-[#1E3A8A] hover:bg-[#1E40AF] h-11 mt-2" disabled={loading || !acceptedTerms || (TURNSTILE_ENABLED && !turnstileToken)} data-testid="register-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
            </Button>
            <TurnstileWidget ref={turnstileRef} onToken={setTurnstileToken} action="register" />
          </form>
          <p className="mt-6 text-sm text-slate-600 text-center">
            Already have an account?{" "}
            <Link to="/login" className="text-blue-700 font-semibold hover:underline">Login</Link>
          </p>
          </>
          )}
        </Card>
      </div>
    </div>
  );
}
